import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Plus, Trash2, Share2, Globe, Copy, ExternalLink, Pencil, Check,
  Linkedin, GraduationCap, BookMarked, Github, Twitter, Award,
  Briefcase, IdCard, Users, ScrollText, Fingerprint, Building2, Phone, MapPin,
  Plane, Wallet, FileText, Handshake, DownloadCloud,
} from "lucide-react";

// Curated identity + social links a professor may want on their dossier.
const IDENTITY_FIELDS = [
  { k: "orcid",          label: "ORCID iD",         placeholder: "0000-0000-0000-0000",           Icon: Fingerprint,   href: (v) => `https://orcid.org/${v}` },
  { k: "google_scholar", label: "Google Scholar",   placeholder: "https://scholar.google.com/…",  Icon: GraduationCap, href: (v) => v },
  { k: "scopus_id",      label: "Scopus Author ID", placeholder: "12345678900",                   Icon: BookMarked,    href: (v) => `https://www.scopus.com/authid/detail.uri?authorId=${v}` },
  { k: "researcher_id",  label: "WoS ResearcherID", placeholder: "AAA-1234-2020",                 Icon: IdCard,        href: (v) => `https://www.webofscience.com/wos/author/record/${v}` },
  { k: "researchgate",   label: "ResearchGate",     placeholder: "https://www.researchgate.net/…", Icon: Users,         href: (v) => v },
  { k: "linkedin",       label: "LinkedIn",         placeholder: "https://linkedin.com/in/…",     Icon: Linkedin,      href: (v) => v },
  { k: "github",         label: "GitHub",           placeholder: "https://github.com/…",          Icon: Github,        href: (v) => v },
  { k: "dblp",           label: "DBLP",             placeholder: "https://dblp.org/pid/…",        Icon: ScrollText,    href: (v) => v },
  { k: "twitter",        label: "X / Twitter",      placeholder: "https://x.com/…",               Icon: Twitter,       href: (v) => v },
  { k: "website",        label: "Personal website", placeholder: "https://…",                     Icon: Globe,         href: (v) => v },
];

const METRIC_FIELDS = [
  { k: "h_index",           label: "h-index" },
  { k: "i10_index",         label: "i10-index" },
  { k: "citations",         label: "Total citations" },
  { k: "total_publications", label: "Total publications" },
  { k: "phd_supervised",    label: "PhD supervised" },
  { k: "ms_supervised",     label: "MS/M.Tech supervised" },
];

export default function Profile() {
  const { user, setUser } = useAuth();
  const [p, setP] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [studio, setStudio] = useState({ proposals: [], papers: [], theses: [], consultancy: [] });

  useEffect(() => {
    api.get("/auth/me").then(r => setP({
      ...r.data,
      full_name: r.data.full_name || user?.full_name || "",
      title: r.data.title || user?.title || "Prof.",
      institution: r.data.institution || user?.institution || "",
      bio: r.data.bio || "",
      orcid: r.data.orcid || "",
      website: r.data.website || "",
      designation: r.data.designation || "",
      department: r.data.department || "",
      phone: r.data.phone || "",
      address: r.data.address || "",
      city: r.data.city || "",
      country: r.data.country || "",
      linkedin: r.data.linkedin || "",
      google_scholar: r.data.google_scholar || "",
      scopus_id: r.data.scopus_id || "",
      researcher_id: r.data.researcher_id || "",
      researchgate: r.data.researchgate || "",
      dblp: r.data.dblp || "",
      github: r.data.github || "",
      twitter: r.data.twitter || "",
      h_index: r.data.h_index ?? "",
      i10_index: r.data.i10_index ?? "",
      citations: r.data.citations ?? "",
      total_publications: r.data.total_publications ?? "",
      phd_supervised: r.data.phd_supervised ?? "",
      ms_supervised: r.data.ms_supervised ?? "",
      expertise: r.data.expertise || [],
      publications: r.data.publications || [],
      past_projects: r.data.past_projects || [],
      past_funding: r.data.past_funding || [],
      awards: r.data.awards || [],
      memberships: r.data.memberships || [],
      patents: r.data.patents || [],
      certifications: r.data.certifications || [],
      collaborators: r.data.collaborators || [],
      travel_grants_history: r.data.travel_grants_history || [],
      consultancy_history: r.data.consultancy_history || [],
      proposals_history: r.data.proposals_history || [],
      import_papers: !!r.data.import_papers,
      import_proposals: !!r.data.import_proposals,
      import_consultancy: !!r.data.import_consultancy,
      import_thesis: !!r.data.import_thesis,
      dossier_public: !!r.data.dossier_public,
    }));
    api.get("/proposals").then(r => setProposals(r.data)).catch(()=>{});
    api.get("/users/me/studio-history").then(r => setStudio(r.data || {})).catch(()=>{});
  }, [user]);

  const save = async () => {
    try {
      const toInt = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
      const payload = {
        dossier_public: p.dossier_public,
        full_name: p.full_name, title: p.title, institution: p.institution,
        bio: p.bio, orcid: p.orcid, website: p.website,
        designation: p.designation, department: p.department,
        phone: p.phone, address: p.address, city: p.city, country: p.country,
        linkedin: p.linkedin, google_scholar: p.google_scholar,
        scopus_id: p.scopus_id, researcher_id: p.researcher_id,
        researchgate: p.researchgate, dblp: p.dblp, github: p.github, twitter: p.twitter,
        h_index: toInt(p.h_index), i10_index: toInt(p.i10_index),
        citations: toInt(p.citations), total_publications: toInt(p.total_publications),
        phd_supervised: toInt(p.phd_supervised), ms_supervised: toInt(p.ms_supervised),
        expertise: p.expertise, publications: p.publications,
        past_projects: p.past_projects, past_funding: p.past_funding,
        awards: p.awards, memberships: p.memberships, patents: p.patents,
        certifications: p.certifications, collaborators: p.collaborators,
        travel_grants_history: p.travel_grants_history,
        consultancy_history: p.consultancy_history,
        proposals_history: p.proposals_history,
        import_papers: !!p.import_papers,
        import_proposals: !!p.import_proposals,
        import_consultancy: !!p.import_consultancy,
        import_thesis: !!p.import_thesis,
      };
      const { data } = await api.put("/users/me/dossier", payload);
      setP({ ...p, ...data });
      // Reflect name/title/institution changes into the auth-context user so the header updates
      if (setUser) setUser(prev => prev ? { ...prev, full_name: data.full_name, title: data.title, institution: data.institution } : prev);
      toast.success("Profile saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const publicUrl = p?.dossier_public ? `${window.location.origin}/p/${p.id}` : "";
  const copyUrl = async () => {
    if (!publicUrl) return;
    try { await navigator.clipboard.writeText(publicUrl); toast.success("Link copied"); }
    catch { toast.error("Copy failed"); }
  };

  if (!p) return <div className="p-8 text-muted-foreground">Loading…</div>;

  const setField = (k, v) => setP(prev => ({ ...prev, [k]: v }));

  return (
    <div className="max-w-5xl mx-auto p-8">
      {/* Header — editable identity */}
      <div className="flex items-start justify-between gap-6 mb-8">
        <div className="flex-1 min-w-0">
          <div className="overline">Profile</div>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <select
              value={p.title || "Prof."}
              onChange={(e)=>setField("title", e.target.value)}
              className="h-11 rounded-md border border-border bg-background px-3 font-display text-2xl"
              data-testid="dossier-name-title"
            >
              {["Prof.","Dr.","Mr.","Ms.","Mrs."].map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <Input
              value={p.full_name}
              onChange={(e)=>setField("full_name", e.target.value)}
              className="!text-4xl md:!text-5xl font-display h-auto py-1 border-none shadow-none focus-visible:ring-0 bg-transparent flex-1 min-w-[280px]"
              placeholder="Your full name"
              data-testid="dossier-name-full"
            />
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2 text-muted-foreground">
            <Input
              value={p.institution}
              onChange={(e)=>setField("institution", e.target.value)}
              className="text-base border-none shadow-none focus-visible:ring-0 bg-transparent max-w-md p-0 h-8"
              placeholder="Your institution"
              data-testid="dossier-institution"
            />
            <span>·</span>
            <span className="text-sm">{user?.discipline || "Discipline not set"}</span>
          </div>
        </div>
        <Button onClick={save} className="rounded-full" data-testid="dossier-save-btn"><Share2 className="w-4 h-4 mr-2"/>Save profile</Button>
      </div>

      {/* Public dossier switch */}
      <div className="card-crisp rounded-lg p-6 mb-6">
        <div className="flex items-center justify-between gap-6">
          <div>
            <div className="font-display text-2xl">Public PI Dossier</div>
            <p className="text-sm text-muted-foreground mt-1 max-w-xl">
              Make your dossier public and share a link with funders and collaborators. Only the fields on this page are exposed — never your proposals.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Label htmlFor="pub" className="text-sm">Public</Label>
            <Switch id="pub" checked={!!p.dossier_public} onCheckedChange={(v)=>setP({...p, dossier_public: v})} data-testid="dossier-public-switch"/>
          </div>
        </div>
        {publicUrl && (
          <div className="mt-4 flex items-center gap-2 border border-border rounded-md px-3 py-2 bg-secondary/30">
            <Globe className="w-4 h-4 text-primary"/>
            <div className="text-sm break-all flex-1" data-testid="dossier-url">{publicUrl}</div>
            <Button size="sm" variant="ghost" onClick={copyUrl}><Copy className="w-4 h-4"/></Button>
            <a href={publicUrl} target="_blank" rel="noreferrer"><Button size="sm" variant="ghost"><ExternalLink className="w-4 h-4"/></Button></a>
          </div>
        )}
      </div>

      {/* About + role + contact */}
      <div className="card-crisp rounded-lg p-6 mb-6 space-y-5">
        <div className="font-display text-2xl flex items-center gap-2"><Briefcase className="w-5 h-5 text-primary"/>About & Role</div>
        <div>
          <Label>Bio</Label>
          <Textarea value={p.bio} onChange={(e)=>setField("bio", e.target.value)} className="min-h-[110px]" placeholder="A short scholarly bio for funders and collaborators…" data-testid="dossier-bio"/>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <div><Label>Designation</Label><Input value={p.designation} onChange={(e)=>setField("designation", e.target.value)} placeholder="Associate Professor" data-testid="dossier-designation"/></div>
          <div><Label>Department</Label><Input value={p.department} onChange={(e)=>setField("department", e.target.value)} placeholder="Dept. of Electrical Engineering" data-testid="dossier-department"/></div>
        </div>
        <div>
          <Label>Expertise (comma separated)</Label>
          <Input
            value={(p.expertise||[]).join(", ")}
            onChange={(e)=>setField("expertise", e.target.value.split(",").map(s=>s.trim()).filter(Boolean))}
            placeholder="Machine Learning, Radiology, Ethics of AI"
            data-testid="dossier-expertise"
          />
        </div>
        <div className="grid md:grid-cols-3 gap-3">
          <div><Label className="flex items-center gap-1"><Phone className="w-3.5 h-3.5"/>Phone</Label><Input value={p.phone} onChange={(e)=>setField("phone", e.target.value)} placeholder="+91 …" data-testid="dossier-phone"/></div>
          <div><Label className="flex items-center gap-1"><Building2 className="w-3.5 h-3.5"/>City</Label><Input value={p.city} onChange={(e)=>setField("city", e.target.value)} placeholder="Bengaluru" data-testid="dossier-city"/></div>
          <div><Label className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5"/>Country</Label><Input value={p.country} onChange={(e)=>setField("country", e.target.value)} placeholder="India" data-testid="dossier-country"/></div>
        </div>
        <div>
          <Label>Address</Label>
          <Input value={p.address} onChange={(e)=>setField("address", e.target.value)} placeholder="Room 202, Block A, Campus Rd." data-testid="dossier-address"/>
        </div>
      </div>

      {/* Academic identity + social links */}
      <div className="card-crisp rounded-lg p-6 mb-6 space-y-4">
        <div className="font-display text-2xl flex items-center gap-2"><Fingerprint className="w-5 h-5 text-primary"/>Identity &amp; Reach</div>
        <p className="text-xs text-muted-foreground -mt-1">Paste your identifiers or full URLs — GrantForge will show clickable links on your public dossier and use them to auto-fill proposals.</p>
        <div className="grid md:grid-cols-2 gap-3">
          {IDENTITY_FIELDS.map((f) => {
            const v = p[f.k] || "";
            const href = v ? f.href(v) : "";
            return (
              <div key={f.k}>
                <Label className="flex items-center gap-1"><f.Icon className="w-3.5 h-3.5 text-primary"/>{f.label}</Label>
                <div className="flex items-center gap-2">
                  <Input
                    value={v}
                    onChange={(e)=>setField(f.k, e.target.value)}
                    placeholder={f.placeholder}
                    data-testid={`dossier-${f.k}`}
                  />
                  {v && href && (
                    <a href={href} target="_blank" rel="noreferrer" title={`Open ${f.label}`}>
                      <Button size="icon" variant="ghost" data-testid={`dossier-${f.k}-open`}><ExternalLink className="w-4 h-4"/></Button>
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Scholar metrics */}
      <div className="card-crisp rounded-lg p-6 mb-6 space-y-4">
        <div className="font-display text-2xl flex items-center gap-2"><Award className="w-5 h-5 text-primary"/>Scholar Metrics</div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {METRIC_FIELDS.map((f) => (
            <div key={f.k}>
              <Label className="text-xs">{f.label}</Label>
              <Input type="number" min="0" value={p[f.k] ?? ""} onChange={(e)=>setField(f.k, e.target.value)} data-testid={`dossier-${f.k}`}/>
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">Update these periodically. They surface at the top of your public dossier and inside AI-drafted proposals as track-record credibility.</p>
      </div>

      {/* Existing dossier lists */}
      <ListEditor title="Publications" testKey="publications" items={p.publications||[]}
        onAdd={()=>setField("publications",[...(p.publications||[]),{id:crypto.randomUUID(),title:"",venue:"",year:"",doi:""}])}
        onChange={(idx,k,v)=>{ const l=[...p.publications]; l[idx]={...l[idx],[k]:v}; setField("publications", l); }}
        onDel={(idx)=>setField("publications", p.publications.filter((_,i)=>i!==idx))}
        fields={[{k:"title",label:"Title",wide:true},{k:"venue",label:"Venue"},{k:"year",label:"Year"},{k:"doi",label:"DOI"},{k:"url",label:"URL"}]}
        summary={(it)=>`${it.title || "(untitled)"} — ${it.venue || ""} ${it.year ? `(${it.year})` : ""}`}
      />

      <ListEditor title="Past Projects" testKey="past-projects" items={p.past_projects||[]}
        onAdd={()=>setField("past_projects",[...(p.past_projects||[]),{id:crypto.randomUUID(),title:"",role:"",duration:"",summary:""}])}
        onChange={(idx,k,v)=>{ const l=[...p.past_projects]; l[idx]={...l[idx],[k]:v}; setField("past_projects", l); }}
        onDel={(idx)=>setField("past_projects", p.past_projects.filter((_,i)=>i!==idx))}
        fields={[{k:"title",label:"Title",wide:true},{k:"role",label:"Role"},{k:"duration",label:"Duration"},{k:"summary",label:"Summary",textarea:true,wide:true}]}
        summary={(it)=>`${it.title || "(untitled)"} — ${it.role || ""}${it.duration ? ` · ${it.duration}` : ""}`}
      />

      <ListEditor title="Past Funding" testKey="past-funding" items={p.past_funding||[]}
        onAdd={()=>setField("past_funding",[...(p.past_funding||[]),{id:crypto.randomUUID(),agency:"",grant_title:"",amount:"",year:"",role:""}])}
        onChange={(idx,k,v)=>{ const l=[...p.past_funding]; l[idx]={...l[idx],[k]:v}; setField("past_funding", l); }}
        onDel={(idx)=>setField("past_funding", p.past_funding.filter((_,i)=>i!==idx))}
        fields={[{k:"agency",label:"Agency"},{k:"grant_title",label:"Grant title",wide:true},{k:"amount",label:"Amount"},{k:"year",label:"Year"},{k:"role",label:"Role"}]}
        summary={(it)=>`${it.grant_title || "(untitled)"} — ${it.agency || ""}${it.year ? ` (${it.year})` : ""}${it.amount ? ` · ${it.amount}` : ""}`}
      />

      {/* New dossier lists */}
      <ListEditor title="Awards & Honors" testKey="awards" items={p.awards||[]}
        onAdd={()=>setField("awards",[...(p.awards||[]),{id:crypto.randomUUID(),title:"",organisation:"",year:""}])}
        onChange={(idx,k,v)=>{ const l=[...p.awards]; l[idx]={...l[idx],[k]:v}; setField("awards", l); }}
        onDel={(idx)=>setField("awards", p.awards.filter((_,i)=>i!==idx))}
        fields={[{k:"title",label:"Title",wide:true},{k:"organisation",label:"Awarded by"},{k:"year",label:"Year"},{k:"note",label:"Note",wide:true}]}
        summary={(it)=>`${it.title || "(untitled)"} — ${it.organisation || ""}${it.year ? ` (${it.year})` : ""}`}
      />

      <ListEditor title="Professional Memberships" testKey="memberships" items={p.memberships||[]}
        onAdd={()=>setField("memberships",[...(p.memberships||[]),{id:crypto.randomUUID(),body:"",role:"Member",since:""}])}
        onChange={(idx,k,v)=>{ const l=[...p.memberships]; l[idx]={...l[idx],[k]:v}; setField("memberships", l); }}
        onDel={(idx)=>setField("memberships", p.memberships.filter((_,i)=>i!==idx))}
        fields={[{k:"body",label:"Body",wide:true},{k:"role",label:"Role"},{k:"since",label:"Since"},{k:"id_no",label:"ID / Grade"}]}
        summary={(it)=>`${it.body || "(unnamed)"} — ${it.role || ""}${it.since ? ` since ${it.since}` : ""}`}
      />

      <ListEditor title="Patents" testKey="patents" items={p.patents||[]}
        onAdd={()=>setField("patents",[...(p.patents||[]),{id:crypto.randomUUID(),title:"",number:"",office:"",year:"",status:"Filed"}])}
        onChange={(idx,k,v)=>{ const l=[...p.patents]; l[idx]={...l[idx],[k]:v}; setField("patents", l); }}
        onDel={(idx)=>setField("patents", p.patents.filter((_,i)=>i!==idx))}
        fields={[{k:"title",label:"Title",wide:true},{k:"number",label:"App./Grant #"},{k:"office",label:"Patent office"},{k:"year",label:"Year"},{k:"status",label:"Status"}]}
        summary={(it)=>`${it.title || "(untitled)"} — ${it.number || ""}${it.status ? ` · ${it.status}` : ""}`}
      />

      <ListEditor title="Certifications" testKey="certifications" items={p.certifications||[]}
        onAdd={()=>setField("certifications",[...(p.certifications||[]),{id:crypto.randomUUID(),title:"",issuer:"",year:""}])}
        onChange={(idx,k,v)=>{ const l=[...p.certifications]; l[idx]={...l[idx],[k]:v}; setField("certifications", l); }}
        onDel={(idx)=>setField("certifications", p.certifications.filter((_,i)=>i!==idx))}
        fields={[{k:"title",label:"Title",wide:true},{k:"issuer",label:"Issuer"},{k:"year",label:"Year"},{k:"url",label:"Credential URL"}]}
        summary={(it)=>`${it.title || "(untitled)"} — ${it.issuer || ""}${it.year ? ` (${it.year})` : ""}`}
      />

      <ListEditor title="Collaborators" testKey="collaborators" items={p.collaborators||[]}
        onAdd={()=>setField("collaborators",[...(p.collaborators||[]),{id:crypto.randomUUID(),name:"",affiliation:"",role:""}])}
        onChange={(idx,k,v)=>{ const l=[...p.collaborators]; l[idx]={...l[idx],[k]:v}; setField("collaborators", l); }}
        onDel={(idx)=>setField("collaborators", p.collaborators.filter((_,i)=>i!==idx))}
        fields={[{k:"name",label:"Name"},{k:"affiliation",label:"Affiliation",wide:true},{k:"role",label:"Role"},{k:"orcid",label:"ORCID"}]}
        summary={(it)=>`${it.name || "(unnamed)"} — ${it.affiliation || ""}${it.role ? ` · ${it.role}` : ""}`}
      />

      {/* History editors — manual entries for work done outside GrantForge */}
      <ListEditor title="Research Proposals Received (history)" testKey="proposals-history" items={p.proposals_history||[]}
        onAdd={()=>setField("proposals_history",[...(p.proposals_history||[]),{id:crypto.randomUUID(),title:"",funder:"",scheme:"",amount:"",year:"",role:"PI",status:"Funded"}])}
        onChange={(idx,k,v)=>{ const l=[...p.proposals_history]; l[idx]={...l[idx],[k]:v}; setField("proposals_history", l); }}
        onDel={(idx)=>setField("proposals_history", p.proposals_history.filter((_,i)=>i!==idx))}
        fields={[{k:"title",label:"Title",wide:true},{k:"funder",label:"Funder"},{k:"scheme",label:"Scheme"},{k:"amount",label:"Amount"},{k:"year",label:"Year"},{k:"role",label:"Role"},{k:"status",label:"Status"}]}
        summary={(it)=>`${it.title || "(untitled)"} — ${it.funder || ""}${it.year ? ` (${it.year})` : ""}${it.status ? ` · ${it.status}` : ""}`}
      />

      <ListEditor title="Travel Grants Received (history)" testKey="travel-history" items={p.travel_grants_history||[]}
        onAdd={()=>setField("travel_grants_history",[...(p.travel_grants_history||[]),{id:crypto.randomUUID(),event:"",funder:"",destination:"",amount:"",year:""}])}
        onChange={(idx,k,v)=>{ const l=[...p.travel_grants_history]; l[idx]={...l[idx],[k]:v}; setField("travel_grants_history", l); }}
        onDel={(idx)=>setField("travel_grants_history", p.travel_grants_history.filter((_,i)=>i!==idx))}
        fields={[{k:"event",label:"Conference / event",wide:true},{k:"funder",label:"Funder"},{k:"destination",label:"Destination"},{k:"amount",label:"Amount"},{k:"year",label:"Year"},{k:"role",label:"Role"}]}
        summary={(it)=>`${it.event || "(unnamed)"} — ${it.destination || ""}${it.year ? ` (${it.year})` : ""}${it.amount ? ` · ${it.amount}` : ""}`}
      />

      <ListEditor title="Consultancy Works Done (history)" testKey="consultancy-history" items={p.consultancy_history||[]}
        onAdd={()=>setField("consultancy_history",[...(p.consultancy_history||[]),{id:crypto.randomUUID(),title:"",client:"",role:"Consultant",amount:"",year:"",duration:""}])}
        onChange={(idx,k,v)=>{ const l=[...p.consultancy_history]; l[idx]={...l[idx],[k]:v}; setField("consultancy_history", l); }}
        onDel={(idx)=>setField("consultancy_history", p.consultancy_history.filter((_,i)=>i!==idx))}
        fields={[{k:"title",label:"Engagement",wide:true},{k:"client",label:"Client / organisation"},{k:"role",label:"Role"},{k:"amount",label:"Fee"},{k:"duration",label:"Duration"},{k:"year",label:"Year"},{k:"summary",label:"Summary",textarea:true,wide:true}]}
        summary={(it)=>`${it.title || "(unnamed)"} — ${it.client || ""}${it.year ? ` (${it.year})` : ""}${it.amount ? ` · ${it.amount}` : ""}`}
      />

      {/* Auto-import from the studio (link app work into the public dossier — user approval required) */}
      <StudioImport
        studio={studio}
        state={{ import_papers: p.import_papers, import_proposals: p.import_proposals, import_consultancy: p.import_consultancy, import_thesis: p.import_thesis }}
        onToggle={(k, v)=>setField(k, v)}
        onImportPapers={()=>{
          const rows = (studio.papers || []).map(x => ({ id: crypto.randomUUID(), title: x.title || "(untitled)", venue: x.target_journal || x.journal_template || "", year: (x.updated_at || "").slice(0,4), doi: "", url: "" }));
          if (!rows.length) return toast.info("No papers in your studio yet");
          const existing = new Set((p.publications || []).map(r => (r.title || "").toLowerCase()));
          const fresh = rows.filter(r => !existing.has((r.title || "").toLowerCase()));
          setField("publications", [...(p.publications || []), ...fresh]);
          toast.success(`Imported ${fresh.length} paper${fresh.length===1?"":"s"} into Publications`);
        }}
        onImportProposals={()=>{
          const rows = (studio.proposals || []).map(x => ({ id: crypto.randomUUID(), title: x.title || "(untitled)", funder: x.funder || "", scheme: x.scheme || "", amount: "", year: (x.updated_at || "").slice(0,4), role: "PI", status: x.status === "submitted" ? "Submitted" : (x.status || "Draft") }));
          if (!rows.length) return toast.info("No proposals in your studio yet");
          const existing = new Set((p.proposals_history || []).map(r => (r.title || "").toLowerCase()));
          const fresh = rows.filter(r => !existing.has((r.title || "").toLowerCase()));
          setField("proposals_history", [...(p.proposals_history || []), ...fresh]);
          toast.success(`Imported ${fresh.length} proposal${fresh.length===1?"":"s"} into history`);
        }}
        onImportConsultancy={()=>{
          const rows = (studio.consultancy || []).map(x => ({ id: crypto.randomUUID(), title: x.title || "(untitled)", client: x.client || "", role: "Consultant", amount: x.budget || "", duration: x.duration || "", year: (x.updated_at || "").slice(0,4), summary: "" }));
          if (!rows.length) return toast.info("No consultancy engagements in your studio yet");
          const existing = new Set((p.consultancy_history || []).map(r => (r.title || "").toLowerCase()));
          const fresh = rows.filter(r => !existing.has((r.title || "").toLowerCase()));
          setField("consultancy_history", [...(p.consultancy_history || []), ...fresh]);
          toast.success(`Imported ${fresh.length} engagement${fresh.length===1?"":"s"} into history`);
        }}
        onImportThesis={()=>{
          const rows = (studio.theses || []).map(x => ({ id: crypto.randomUUID(), title: x.title || "(untitled)", role: "Supervisor", duration: "", summary: "" }));
          if (!rows.length) return toast.info("No theses in your studio yet");
          const existing = new Set((p.past_projects || []).map(r => (r.title || "").toLowerCase()));
          const fresh = rows.filter(r => !existing.has((r.title || "").toLowerCase()));
          setField("past_projects", [...(p.past_projects || []), ...fresh]);
          toast.success(`Imported ${fresh.length} thesis project${fresh.length===1?"":"s"}`);
        }}
      />

      <div className="card-crisp rounded-lg p-6 mt-6">
        <div className="font-display text-2xl mb-2">Studio activity</div>
        <div className="text-sm text-muted-foreground">You have <b>{proposals.length}</b> proposals · <b>{proposals.filter(p=>p.status==="draft").length}</b> drafts.</div>
      </div>
    </div>
  );
}

/**
 * StudioImport: professor-controlled linking of GrantForge Studio activity into the public dossier.
 * Shows counts for Proposals / Papers / Theses / Consultancy created inside the app.
 * Each row has a Switch (persisted preference) and an Import button that copies the items
 * into the relevant editable list so the user can curate before saving.
 */
function StudioImport({ studio, state, onToggle, onImportPapers, onImportProposals, onImportConsultancy, onImportThesis }) {
  const rows = [
    { k: "import_papers",      label: "Paper Publications",     icon: FileText,   count: (studio.papers || []).length,       onImport: onImportPapers,      hint: "Publications you drafted inside GrantForge" },
    { k: "import_proposals",   label: "Research Proposals",     icon: Wallet,     count: (studio.proposals || []).length,    onImport: onImportProposals,   hint: "Proposals you worked on inside GrantForge" },
    { k: "import_consultancy", label: "Consultancy Works",      icon: Handshake,  count: (studio.consultancy || []).length,  onImport: onImportConsultancy, hint: "Consultancy engagements drafted in GrantForge" },
    { k: "import_thesis",      label: "Thesis Supervision",     icon: BookMarked, count: (studio.theses || []).length,       onImport: onImportThesis,      hint: "Theses in your studio (added to Past Projects)" },
  ];
  return (
    <div className="card-crisp rounded-lg p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-display text-2xl flex items-center gap-2"><DownloadCloud className="w-5 h-5 text-primary"/>Link to my GrantForge activity</div>
          <p className="text-xs text-muted-foreground mt-1 max-w-2xl">Nothing is imported without your approval. Toggle a source ON to include a summary of that studio activity in your public dossier, and click <b>Import now</b> to snapshot the items into your editable history.</p>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {rows.map(({ k, label, icon: Icon, count, onImport, hint }) => (
          <div key={k} className="border border-border rounded-md p-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-primary/10 text-primary grid place-items-center shrink-0"><Icon className="w-5 h-5"/></div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium">{label} <span className="text-xs text-muted-foreground">· {count} in studio</span></div>
              <div className="text-xs text-muted-foreground truncate">{hint}</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Switch checked={!!state[k]} onCheckedChange={(v)=>onToggle(k, v)} data-testid={`toggle-${k}`}/>
              <Button size="sm" variant="outline" onClick={onImport} disabled={count === 0} data-testid={`import-${k}`}>
                <DownloadCloud className="w-3.5 h-3.5 mr-1"/>Import now
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * ListEditor: adds an explicit view/edit mode.
 * - Each row is shown as a compact summary with an "Edit" (pencil) button.
 * - Clicking Edit expands the row into an inline form; clicking Done closes it.
 * - "Add" appends a new blank row already in edit mode.
 */
function ListEditor({ title, testKey, items, onAdd, onChange, onDel, fields, summary }) {
  const testBase = testKey || title.toLowerCase().replace(/\s/g, "-");
  const [openIdx, setOpenIdx] = useState(new Set());
  const isOpen = (i) => openIdx.has(i);
  const toggle = (i) => setOpenIdx(prev => {
    const next = new Set(prev);
    next.has(i) ? next.delete(i) : next.add(i);
    return next;
  });

  const handleAdd = () => {
    onAdd();
    // Open the newly added row (it becomes the last index)
    setTimeout(() => setOpenIdx(prev => {
      const next = new Set(prev);
      next.add(items.length);
      return next;
    }), 0);
  };

  return (
    <div className="card-crisp rounded-lg p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="font-display text-2xl">{title}</div>
        <Button size="sm" variant="outline" onClick={handleAdd} data-testid={`dossier-add-${testBase}`}>
          <Plus className="w-4 h-4 mr-1"/>Add
        </Button>
      </div>
      {items.length === 0 && <div className="text-sm text-muted-foreground italic">Nothing here yet. Click <b>Add</b> to create the first one.</div>}
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={it.id || i} className="border border-border rounded-md">
            {/* Summary row */}
            <div className="flex items-center gap-2 p-3">
              <div className="flex-1 text-sm truncate">
                <span className="font-medium">{summary ? summary(it) : (it.title || "(untitled)")}</span>
              </div>
              <Button size="sm" variant={isOpen(i) ? "default" : "outline"} onClick={()=>toggle(i)} data-testid={`dossier-edit-${testBase}-${i}`}>
                {isOpen(i) ? <><Check className="w-3.5 h-3.5 mr-1"/>Done</> : <><Pencil className="w-3.5 h-3.5 mr-1"/>Edit</>}
              </Button>
              <button
                onClick={()=>{ onDel(i); setOpenIdx(prev => { const next = new Set(); prev.forEach(x => x < i && next.add(x)); return next; }); }}
                className="p-2 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                title="Remove"
                data-testid={`dossier-remove-${testBase}-${i}`}
              >
                <Trash2 className="w-4 h-4"/>
              </button>
            </div>
            {/* Editor */}
            {isOpen(i) && (
              <div className="border-t border-border p-3 grid md:grid-cols-2 gap-2 bg-secondary/20">
                {fields.map((f) => (
                  <div key={f.k} className={f.wide ? "md:col-span-2" : ""}>
                    <Label className="text-xs">{f.label}</Label>
                    {f.textarea
                      ? <Textarea value={it[f.k] || ""} onChange={(e)=>onChange(i, f.k, e.target.value)}/>
                      : <Input value={it[f.k] || ""} onChange={(e)=>onChange(i, f.k, e.target.value)}/>}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
